/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/MWC/ng-zorro-antd/blob/master/LICENSE
 */

import { Direction, Directionality } from '@angular/cdk/bidi';
import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { filter, startWith, takeUntil } from 'rxjs/operators';

import { MwcConfigKey, MwcConfigService, WithConfig } from 'mwc/core/config';
import { BooleanInput } from 'mwc/core/types';
import { InputBoolean } from 'mwc/core/util';
import { MwcIconDirective } from 'mwc/icon';

export type MwcButtonType = 'primary' | 'default' | 'default-color' | 'dashed' | 'link' | 'link-color' | 'text' | 'text-color' | null ;
export type MwcButtonShape = 'circle' | 'round' | null;
export type MwcButtonSize = 'large' | 'default' | 'small' | 'xl' | '2xl';

const NZ_CONFIG_MODULE_NAME: MwcConfigKey = 'button';

@Component({
  selector: 'button[mwc-button], a[mwc-button]',
  exportAs: 'mwcButton',
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <span mwc-icon mwcType="loading" *ngIf="mwcLoading"></span>
    <ng-content></ng-content>
  `,
  host: {
    class: 'mwc-btn',
    '[class.mwc-btn-primary]': `mwcType === 'primary'`,
    '[class.mwc-btn-dashed]': `mwcType === 'dashed'`,
    '[class.mwc-btn-color]': `mwcType === 'default-color'`,
    '[class.mwc-btn-link]': `mwcType === 'link'`,
    '[class.mwc-btn-link-color]': `mwcType === 'link-color'`,
    '[class.mwc-btn-text]': `mwcType === 'text'`,
    '[class.mwc-btn-text-color]': `mwcType === 'text-color'`,
    '[class.mwc-btn-circle]': `mwcShape === 'circle'`,
    '[class.mwc-btn-round]': `mwcShape === 'round'`,
    '[class.mwc-btn-lg]': `mwcSize === 'large'`,
    '[class.mwc-btn-sm]': `mwcSize === 'small'`,
    '[class.mwc-btn-xl]': `mwcSize === 'xl'`,
    '[class.mwc-btn-2xl]': `mwcSize === '2xl'`,
    '[class.mwc-btn-dangerous]': `mwcDanger`,
    '[class.mwc-btn-loading]': `mwcLoading`,
    '[class.mwc-btn-background-ghost]': `mwcGhost`,
    '[class.mwc-btn-block]': `mwcBlock`,
    '[class.mwc-input-search-button]': `mwcSearch`,
    '[class.mwc-btn-rtl]': `dir === 'rtl'`,
    '[attr.tabindex]': 'disabled ? -1 : (tabIndex === null ? null : tabIndex)',
    '[attr.disabled]': 'disabled || null'
  }
})
export class MwcButtonComponent implements OnDestroy, OnChanges, AfterViewInit, AfterContentInit, OnInit {
  readonly _mwcModuleName: MwcConfigKey = NZ_CONFIG_MODULE_NAME;
  static ngAcceptInputType_mwcBlock: BooleanInput;
  static ngAcceptInputType_mwcGhost: BooleanInput;
  static ngAcceptInputType_mwcSearch: BooleanInput;
  static ngAcceptInputType_mwcLoading: BooleanInput;
  static ngAcceptInputType_mwcDanger: BooleanInput;
  static ngAcceptInputType_disabled: BooleanInput;

  @ContentChild(MwcIconDirective, { read: ElementRef }) mwcIconDirectiveElement!: ElementRef;
  @Input() @InputBoolean() mwcBlock: boolean = false;
  @Input() @InputBoolean() mwcGhost: boolean = false;
  @Input() @InputBoolean() mwcSearch: boolean = false;
  @Input() @InputBoolean() mwcLoading: boolean = false;
  @Input() @InputBoolean() mwcDanger: boolean = false;
  @Input() @InputBoolean() mwcColor: boolean = false;
  @Input() @InputBoolean() disabled: boolean = false;
  @Input() tabIndex: number | string | null = null;
  @Input() mwcType: MwcButtonType = null;
  @Input() mwcShape: MwcButtonShape = null;
  @Input() @WithConfig() mwcSize: MwcButtonSize = 'default';
  dir: Direction = 'ltr';
  private destroy$ = new Subject<void>();
  private loading$ = new Subject<boolean>();

  insertSpan(nodes: NodeList, renderer: Renderer2): void {
    nodes.forEach(node => {
      if (node.nodeName === '#text') {
        const span = renderer.createElement('span');
        const parent = renderer.parentNode(node);
        renderer.insertBefore(parent, span, node);
        renderer.appendChild(span, node);
      }
    });
  }

  assertIconOnly(element: HTMLButtonElement, renderer: Renderer2): void {
    const listOfNode = Array.from(element.childNodes);
    const iconCount = listOfNode.filter(node => {
      const iconChildNodes = Array.from(node.childNodes || []);
      return node.nodeName === 'SPAN' && iconChildNodes.length > 0 && iconChildNodes.every(ic => ic.nodeName === 'svg');
    }).length;
    const noText = listOfNode.every(node => node.nodeName !== '#text');
    // ignore icon
    const noSpan = listOfNode
      .filter(node => {
        const iconChildNodes = Array.from(node.childNodes || []);
        return !(
          node.nodeName === 'SPAN' &&
          iconChildNodes.length > 0 &&
          iconChildNodes.every(ic => ic.nodeName === 'svg')
        );
      })
      .every(node => node.nodeName !== 'SPAN');
    const isIconOnly = noSpan && noText && iconCount >= 1;
    if (isIconOnly) {
      renderer.addClass(element, 'mwc-btn-icon-only');
    }
  }

  constructor(
    private ngZone: NgZone,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    public mwcConfigService: MwcConfigService,
    @Optional() private directionality: Directionality
  ) {
    this.mwcConfigService
      .getConfigChangeEventForComponent(NZ_CONFIG_MODULE_NAME)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.markForCheck();
      });
  }

  ngOnInit(): void {
    this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe((direction: Direction) => {
      this.dir = direction;
      this.cdr.detectChanges();
    });

    this.dir = this.directionality.value;

    this.ngZone.runOutsideAngular(() => {
      // Caretaker note: this event listener could've been added through `host.click` or `HostListener`.
      // The compiler generates the `ɵɵlistener` instruction which wraps the actual listener internally into the
      // function, which runs `markDirty()` before running the actual listener (the decorated class method).
      // Since we're preventing the default behavior and stopping event propagation this doesn't require Angular to run the change detection.
      fromEvent<MouseEvent>(this.elementRef.nativeElement, 'click', { capture: true })
        .pipe(takeUntil(this.destroy$))
        .subscribe(event => {
          if ((this.disabled && (event.target as HTMLElement)?.tagName === 'A') || this.mwcLoading) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { mwcLoading } = changes;
    if (mwcLoading) {
      this.loading$.next(this.mwcLoading);
    }
  }

  ngAfterViewInit(): void {
    this.assertIconOnly(this.elementRef.nativeElement, this.renderer);
    this.insertSpan(this.elementRef.nativeElement.childNodes, this.renderer);
  }

  ngAfterContentInit(): void {
    this.loading$
      .pipe(
        startWith(this.mwcLoading),
        filter(() => !!this.mwcIconDirectiveElement),
        takeUntil(this.destroy$)
      )
      .subscribe(loading => {
        const nativeElement = this.mwcIconDirectiveElement.nativeElement;
        if (loading) {
          this.renderer.setStyle(nativeElement, 'display', 'none');
        } else {
          this.renderer.removeStyle(nativeElement, 'display');
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
